import { useCallback, useEffect } from 'react';
import { useOptionalCallQueue } from '../contexts/CallQueueContext';

/**
 * Хук для глобальных keyboard shortcuts очереди звонков
 *
 * Shortcuts:
 * - Ctrl/Cmd + K — Toggle панели
 * - Ctrl/Cmd + [ — Предыдущая карточка
 * - Ctrl/Cmd + ] — Следующая карточка
 * - Ctrl/Cmd + 1-9 — Быстрый выбор карточки по позиции
 */
export function useCallQueueShortcuts(): void {
  const callQueue = useOptionalCallQueue();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!callQueue) return;

      const { queue, isExpanded, selectedCallId, togglePanel, selectCall, enterCardMode } =
        callQueue;

      // Проверяем, что фокус не в поле ввода
      const target = e.target as HTMLElement | null;
      const tagName = (target?.tagName || '').toLowerCase();
      const isInInput =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target?.isContentEditable;

      // Ctrl/Cmd + K — Toggle панели
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        // Позволяем работать даже в полях ввода
        e.preventDefault();
        togglePanel();
        return;
      }

      // Остальные shortcuts только вне полей ввода
      if (isInInput) return;

      // Ctrl/Cmd + [ — Предыдущая карточка
      if ((e.ctrlKey || e.metaKey) && e.key === '[') {
        e.preventDefault();
        if (!isExpanded || queue.length === 0) return;

        const currentIndex = selectedCallId
          ? queue.findIndex((c) => c.callId === selectedCallId)
          : -1;

        if (currentIndex <= 0) {
          // Переход на последнюю карточку
          const lastCall = queue[queue.length - 1];
          selectCall(lastCall.callId);
        } else {
          // Переход на предыдущую карточку
          const prevCall = queue[currentIndex - 1];
          selectCall(prevCall.callId);
        }
        return;
      }

      // Ctrl/Cmd + ] — Следующая карточка
      if ((e.ctrlKey || e.metaKey) && e.key === ']') {
        e.preventDefault();
        if (!isExpanded || queue.length === 0) return;

        const currentIndex = selectedCallId
          ? queue.findIndex((c) => c.callId === selectedCallId)
          : -1;

        if (currentIndex === -1 || currentIndex >= queue.length - 1) {
          // Переход на первую карточку
          const firstCall = queue[0];
          selectCall(firstCall.callId);
        } else {
          // Переход на следующую карточку
          const nextCall = queue[currentIndex + 1];
          selectCall(nextCall.callId);
        }
        return;
      }

      // Ctrl/Cmd + 1-9 — Быстрый выбор карточки по позиции
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const index = parseInt(e.key, 10) - 1;
        if (index < queue.length) {
          const call = queue[index];
          selectCall(call.callId);
          // Открываем карточку
          if (isExpanded) {
            enterCardMode(call.callId);
          }
        }
        return;
      }
    },
    [callQueue]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export default useCallQueueShortcuts;
